// Sawyer 15 workweek-hours-ot (E2) — one fetch of /timesheets/workweek shared
// by the This-week panel and the overtime banner, so they cannot disagree
// (Principle 1). `date` null = "this week" as the server's tenant clock sees
// it (the browser's clock is not the facility's).

import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchWorkweekHours, type WorkweekHoursView } from '../../lib/workweek-api.js';

export type WorkweekState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; data: WorkweekHoursView };

export function useWorkweekHours(
  apiUrl: string,
  date: string | null,
  opts: { enabled?: boolean; refreshKey?: number } = {},
): { state: WorkweekState; reload: () => void } {
  const enabled = opts.enabled ?? true;
  const [state, setState] = useState<WorkweekState>({ kind: 'loading' });
  const [nonce, setNonce] = useState(0);
  const seq = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    const mine = ++seq.current;
    setState({ kind: 'loading' });
    void fetchWorkweekHours(apiUrl, date ?? undefined).then((res) => {
      if (mine !== seq.current) return; // a newer request superseded this one
      setState(res.ok ? { kind: 'ready', data: res.data } : { kind: 'error' });
    });
  }, [apiUrl, date, enabled, nonce, opts.refreshKey]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { state, reload };
}
